import { isAbortError, ignoreAbortError } from '@/utils/abort-util'
import type { GetCurrentUserWorkspaceMembershipsFn } from '@/features/iam'
import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  Observable,
  skipWhile,
} from 'rxjs'
import type {
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import { type Member } from '@/features/iam/members/Member'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import { ViewModelList } from '@/infra/view-model-list'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { shareLatest, whileActive } from '@/utils/rx-util'
import { tapAction, toStream } from '@/utils/mobx-util'
import {
  type FetchInventoryItemsByIdsFn,
  type ListInventoryItemsFn,
} from '@/features/inventory'
import { type BundleArchivedFilterOptions } from '@/features/admin/bundle/BundleStore'
import { BundleSearchViewModel } from './BundleSearchViewModel'
import { type BundleStore } from './BundleStore'
import { ArchiveBundleDialogViewModel } from '@/features/admin/bundle/ArchiveBundleDialog/ArchiveBundleDialogViewModel'
import {
  BundleDialogViewModel,
  type BundleDialogVariant,
} from '@/features/admin/bundle/BundleDialog/BundleDialogViewModel'
import { UnarchiveBundleDialogViewModel } from '@/features/admin/bundle/UnarchiveBundleDialog/UnarchiveBundleDialogViewModel'
import {
  type BundleDialogs,
  WorkspaceBundleViewModel,
} from './WorkspaceBundleViewModel'
import { WorkspaceSearchViewModel } from './WorkspaceSearchViewModel'
import { WorkspaceSelectorViewModel } from '@/features/inventory/admin/WorkspaceSelectorViewModel'
import { BulkDuplicateBundleDialogViewModel } from '@/features/admin/bundle/BulkDuplicateBundleDialog/BulkDuplicateBundleDialogViewModel'
import { BulkArchiveBundleDialogViewModel } from '@/features/admin/bundle/BulkArchiveBundleDialog/BulkArchiveBundleDialogViewModel'
import { BulkEditPriceDialogViewModel } from '@/features/admin/bundle/BulkEditPriceDialog/BulkEditPriceDialogViewModel'
import { BulkApplyDiscountDialogViewModel } from '@/features/admin/bundle/BulkApplyDiscountDialog/BulkApplyDiscountDialogViewModel'

/**
 * Workspace filter for Bundles.
 */
export type BundleManagerWorkspaceFilter = WorkspaceBundles | AllWorkspaces

/**
 * View all bundles in all workspaces.
 */
export interface AllWorkspaces {
  type: 'AllWorkspaces'
}

/**
 * View bundles in a workspace.
 */
export interface WorkspaceBundles {
  type: 'Workspace'
  workspace: Workspace
}

/**
 * View model for bundles.
 */
export class BundleManagerViewModel
  implements ActivationLifecycle, BundleDialogs
{
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * The selector updates and the bundle search wait for `fetch` to make the
   * default workspace selection, which would otherwise override them.
   */
  private readonly _defaultSelectionApplied$ = new BehaviorSubject(false)

  private readonly _workspaceVms

  /**
   * Update workspace filter when current workspace is changed.
   */
  private readonly _workspaceSelectorUpdates$

  /**
   * The currently selected archived status tab.
   */
  @observable accessor currentArchivedStatusTab: BundleArchivedFilterOptions =
    'Active'

  /**
   * Workspace filter.
   */
  @observable
  accessor workspaceFilter: BundleManagerWorkspaceFilter = {
    type: 'Workspace',
    workspace: Workspace.none,
  }

  readonly workspaceSearcher

  readonly searcher

  readonly workspaceSelectorVm

  readonly createBundleVm

  readonly editBundleVm

  readonly archiveBundleVm

  readonly unarchiveBundleVm

  readonly duplicateBundleVm

  readonly bulkDuplicateBundleVm

  readonly bulkArchiveBundleVm

  readonly bulkEditPriceVm

  readonly bulkApplyDiscountVm

  constructor(
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
    private readonly _bundleStore: BundleStore,
    private readonly _searchInventoryItems: ListInventoryItemsFn,
    private readonly _fetchInventoryItemsByIds: FetchInventoryItemsByIdsFn,
    private readonly _getWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn,
    private readonly _currentMember$: Observable<Member>,
  ) {
    this.workspaceSearcher = new WorkspaceSearchViewModel(this._bundleStore)

    this.searcher = new BundleSearchViewModel(
      this._bundleStore,
      this._createAlertRetryWrapper,
      this._getWorkspaceMemberships,
      this.workspaceSearcher,
    )

    this.workspaceSelectorVm = new WorkspaceSelectorViewModel(
      this._getWorkspaceMemberships,
    )

    const createBundleDialogViewModel = (variant: BundleDialogVariant) =>
      new BundleDialogViewModel(
        variant,
        this._searchInventoryItems,
        this._fetchInventoryItemsByIds,
        this._bundleStore,
        this.refreshBundles,
        () => this.searcher.workspace,
        this.workspaceSelectorVm,
        this._newAlert,
        this._createAlertErrorHandler,
        this._createAlertRetryWrapper,
      )

    this.createBundleVm = createBundleDialogViewModel('Create')
    this.editBundleVm = createBundleDialogViewModel('Edit')
    this.duplicateBundleVm = createBundleDialogViewModel('Duplicate')

    this.archiveBundleVm = new ArchiveBundleDialogViewModel(
      this._bundleStore,
      this.refreshBundles,
      this._newAlert,
      this._createAlertErrorHandler,
    )

    this.unarchiveBundleVm = new UnarchiveBundleDialogViewModel(
      this._bundleStore,
      this.refreshBundles,
      this._newAlert,
      this._createAlertErrorHandler,
    )

    this.bulkDuplicateBundleVm = new BulkDuplicateBundleDialogViewModel(
      this.workspaceSelectorVm,
      this._newAlert,
      this._bundleStore,
      this.refreshBundles,
    )

    this.bulkArchiveBundleVm = new BulkArchiveBundleDialogViewModel(
      this._bundleStore,
      this._newAlert,
      this.refreshBundles,
    )

    this.bulkEditPriceVm = new BulkEditPriceDialogViewModel(
      this._bundleStore,
      this._newAlert,
      this.refreshBundles,
    )

    this.bulkApplyDiscountVm = new BulkApplyDiscountDialogViewModel(
      this._bundleStore,
      this._newAlert,
      this.refreshBundles,
    )

    this._workspaceSelectorUpdates$ = combineLatest([
      this._currentMember$,
      toStream(() => this.searcher.allWorkspaceIdsWithBundles, true),
    ]).pipe(
      skipWhile(([, ids]) => ids.length < 1),
      distinctUntilChanged(
        ([prev], [curr]) => prev.workspace.id === curr.workspace.id,
      ),
      tapAction(([member, ids]) => {
        if (ids.some((id) => id === member.workspace.id)) {
          this.selectWorkspaceForFiltering(member.workspace)
        }
      }),
      shareLatest(),
    )

    this._workspaceVms = new ViewModelList<Workspace, WorkspaceBundleViewModel>(
      {
        create: (workspace) =>
          new WorkspaceBundleViewModel({
            workspace,
            searcher: this.searcher,
            isWorkspaceSelected: () =>
              this.workspaceFilter.type === 'Workspace',
            dialogs: this,
          }),
        models: () => this.workspaces,
        activate: (vm) => vm.activate(),
        deactivate: (vm) => vm.deactivate(),
      },
    )

    this._lifecycle.manage(this.workspaceSelectorVm)
    this._lifecycle.manage(this.createBundleVm)
    this._lifecycle.manage(this.editBundleVm)
    this._lifecycle.manage(this.duplicateBundleVm)
    this._lifecycle.manage(this._workspaceVms)
    this._lifecycle.manage(
      this._workspaceSelectorUpdates$.pipe(
        whileActive(this._defaultSelectionApplied$),
      ),
    )
    this._lifecycle.manage(
      new Observable<never>(() => {
        this.searcher.activate()
        return () => this.searcher.deactivate()
      }).pipe(whileActive(this._defaultSelectionApplied$)),
    )
  }

  /**
   * All Workspaces that have bundles
   */
  @computed
  get workspaces() {
    return this.workspaceSearcher.filteredItems.map((x) => x.workspace)
  }

  /**
   * The bundle view models of the workspaces with bundles.
   */
  @computed
  get workspaceVms() {
    return this._workspaceVms.items
  }

  /**
   * The workspace selected in the heading; `Workspace.none` for all workspaces.
   */
  @computed
  get selectedWorkspace() {
    return this.workspaceFilter.type === 'AllWorkspaces'
      ? Workspace.none
      : this.workspaceFilter.workspace
  }

  /**
   * The name of the filtered workspace; `null` when showing all workspaces.
   */
  @computed
  get currentWorkspaceName() {
    return this.selectedWorkspace === Workspace.none
      ? null
      : this.selectedWorkspace.name
  }

  @computed
  get hasBundles() {
    return this.searcher.allBundles.length !== 0
  }

  @computed
  get canManageBundles() {
    return this.searcher.canManageBundles
  }

  @computed
  get canSearch() {
    return this.workspaceSearcher.activeWorkspaces.length >= 5
  }

  @computed
  get workspacesUseDropdown() {
    return this.canManageBundles
      ? this.canSearch || this.workspaces.length === 1
      : this.workspaces.length > 1
  }

  /**
   * Set the archived status tab.
   */
  @action.bound
  setCurrentArchivedStatusTab(archivedStatusTab: BundleArchivedFilterOptions) {
    if (archivedStatusTab === this.currentArchivedStatusTab) {
      return
    }

    this.currentArchivedStatusTab = archivedStatusTab
    this.searcher.setCurrentArchivedStatusFilter(archivedStatusTab)
  }

  /**
   * Selects the workspace to filter by.
   */
  @action.bound
  selectWorkspaceForFiltering(workspace: Workspace) {
    this.workspaceFilter =
      workspace === Workspace.none ||
      !this.workspaces.find((ws) => ws.id === workspace.id)
        ? { type: 'AllWorkspaces' }
        : { workspace, type: 'Workspace' }

    this.searcher.setWorkspace(
      workspace === Workspace.none ? undefined : workspace,
    )

    this._workspaceVms.items
      .find((ws) => ws.id === workspace.id)
      ?.clearSelectedBundle()
  }

  /**
   * Reloads the bundles after a change, switching to `workspace` if given.
   */
  @action.bound
  async refreshBundles(workspace?: Workspace) {
    try {
      await this._fetchWorkspaces(this._lifecycle.signal)
    } catch {
      return
    }

    if (workspace) {
      this.selectWorkspaceForFiltering(workspace)
    }
    this.searcher.refreshItems()
  }

  fetch = task(async () => {
    const signal = this._lifecycle.signal
    await this._fetchWorkspaces(signal)
    signal.throwIfAborted()

    if (this.canManageBundles) {
      this.selectWorkspaceForFiltering(Workspace.none)
    } else if (this.workspaces.length > 0) {
      this.selectWorkspaceForFiltering(this.workspaces[0])
    }

    this._defaultSelectionApplied$.next(true)
  })

  /** @inheritDoc */
  activate() {
    this._defaultSelectionApplied$.next(false)
    this._lifecycle.activate()
    void this.fetch().catch(ignoreAbortError())
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Fetches the workspaces with bundles. When the retry alert is dismissed,
   * the workspaces fetched before are kept; only an abort rejects.
   */
  private async _fetchWorkspaces(signal: AbortSignal) {
    const withRetry = this._createAlertRetryWrapper(
      'Loading the bundle workspaces failed',
      signal,
    )
    try {
      await withRetry(() =>
        this.workspaceSearcher.refreshWorkspacesWithBundles(signal),
      )
    } catch (err) {
      if (isAbortError(err)) {
        throw err
      }
    }
  }
}
